<template>
  <b-container fluid class="d-flex flex-column py-3">
    <b-row class="mb-3">
      <b-col>
        <b-form-select
          v-model="inputId"
          ref="selectInputId"
          :options="rules"
          @change="onChangeInputId"
        ></b-form-select>
      </b-col>
    </b-row>
    <b-table
      v-if="visibleList"
      hover
      no-border-collapse
      selectable
      select-mode="single"
      sticky-header
      thead-tr-class="small"
      tbody-tr-class="h5"
      ref="selectableTable"
      :items="items"
      :fields="fields"
      @row-selected="onRowSelected"
    >
      <!-- 予定/実績 -->
      <template #cell(orderResult)="data"
        >{{ data.item.orderCount }}&nbsp;/&nbsp;{{ data.item.resultCount }}</template
      >
      <!-- 実績へ -->
      <template #cell(toDetail)>
        <i class="bi bi-chevron-right"></i>
      </template>
    </b-table>
    <b-row v-if="visibleList">
      <b-col>
        <b-form-file v-model="file" plain @input="onClickInput"></b-form-file>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { getCompareOrderListAPI, postCompeareOrderInputAPI } from '@/api';
import {
  convertItemFormat,
  createInputSelectItems,
  getCompareHeaderFieldsList,
  getDateValue
} from '@/compare';
import { csvParse } from '@/CommonFunctions';
import { columnType, dataLayout, dataType, dtFormat } from '@/constants';
import { DateTime } from 'luxon';

export default {
  name: 'CompareOrderInput',
  data() {
    return {
      inputId: null, // 取込ID
      rules: [], // 取込ルールリスト
      fields: null, // 項目
      items: null, // 値
      file: null // 取込ファイル
    };
  },
  async mounted() {
    // 画面を初期化
    this.$emit('initView', { viewId: this.$options.name, useToolButtons: false });

    // 取込ルールリスト作成
    this.rules = createInputSelectItems(this.$store.getters.inputs);

    // 前画面からパラメータ取得
    const inpt = this.$store.getters.currentInput;
    if (inpt) {
      // 取込ルールを選択状態にする
      await this.onChangeInputId(inpt.inputId);
    }
  },
  computed: {
    visibleList: function () {
      return this.inputId !== null;
    }
  },
  methods: {
    /**
     * 照合対象変更時
     * @param {string} event イベント引数(読取ID)
     */
    onChangeInputId: async function (event) {
      // 取込IDを保持
      this.$store.commit('setInputId', event);
      this.inputId = Number(event);

      // 画面情報の属性をセット
      const inpt = this.$store.getters.currentInput;
      if (!inpt) {
        this.inputId = null;
        this.$store.commit('setMessage', null);
        return;
      }

      // 一覧表示項目選択
      this.inputId = inpt.inputId;
      const compare = this.$store.getters.currentCompare;
      if (compare) {
        const fields = getCompareHeaderFieldsList(compare);
        this.fields = fields.map(function (x) {
          return { key: x.key, label: x.label, thClass: 'p-1', tdClass: 'px-1' };
        });
        this.fields.push({ key: 'orderResult', label: '予/実', thClass: 'p-1', tdClass: 'px-1' });
        this.fields.push({
          key: 'toDetail',
          label: '',
          thClass: 'p-1',
          tdClass: 'text-right px-0'
        });

        // 照合予定情報リスト検索
        await this.getCompareOrderList(event, this.$store.getters.currentCompare);
      } else {
        this.fields.splice(0);
        this.items.splice(0);
      }

      this.$store.commit('setMessage', '照合予定を取込/選択します');

      // 取込ルールのフォーカスを外す
      this.$refs.selectInputId?.blur();
    },
    /**
     * 照合予定行選択時
     * @param {object} selectedItems 選択項目(照合予定)
     */
    onRowSelected: async function (selectedItems) {
      // 照合実績画面へ
      const url = `/CompareResult/${selectedItems[0].compareOrderNo}`;
      this.$router.push({ path: url });

      // 次画面へのパラメータセット
      this.$store.commit('incrementHistoryCount');
    },
    /**
     * 取込ボタンクリック時
     */
    onClickInput: async function () {
      if (!this.file) {
        this.$store.commit('setMessage', '照合予定を取込/選択します');
        return;
      }

      // CSVデータを解析する
      const inpt = this.$store.getters.currentInput;
      const json = await csvParse(this.file, inpt.delimiter, inpt.fileHeader === 1);
      this.file = null;
      if (!json) {
        this.$store.commit('setMessage', 'CSVファイルの解析に失敗');
        return;
      }

      const nowValue = DateTime.now();
      const dateText = nowValue.toFormat(dtFormat.DATE);
      const timeText = nowValue.toFormat(dtFormat.TIME);

      const rows = [];
      for (let i = 0; i < json.length; i++) {
        // 項目数が一致しない場合はエラー
        if (json[i].length !== inpt.length) {
          this.$store.commit('setMessage', 'CSV解析エラー(項目数不一致)');
          return;
        }

        const row = {};
        for (let idetail of inpt.details) {
          // 列番号の位置が該当行に存在しない場合はエラー
          if (idetail.columnNo > json[i].length) {
            this.$store.commit('setMessage', 'CSV解析エラー(列番号定義)');
            return;
          }

          const value = json[i][idetail.columnNo - 1];
          switch (idetail.columnType) {
            case columnType.TEXT: // 文字列
              row[idetail.columnId] = value;
              break;
            case columnType.NUMBER: // 数値
              if (isNaN(value)) {
                this.$store.commit('setMessage', 'CSV解析エラー(数値不正)');
                return;
              }
              row[idetail.columnId] = Number(value).toString();
              break;
            case columnType.DATE: // 日付
              row[idetail.columnId] = getDateValue(value, idetail.dateFormat);
              if (!row[idetail.columnId]) {
                this.$store.commit('setMessage', 'CSV解析エラー(日付不正)');
                return;
              }
              break;
            default:
              this.$store.commit('setMessage', 'CSV解析エラー(列区分不正)');
              return;
          }
        }
        rows.push(row);
      }

      // 取込情報に従い照合予定情報を解析する
      const cmpr = this.$store.getters.currentCompare;

      // 照合予定登録形式に変換する
      const items = [];
      for (let i = 0; i < rows.length; i++) {
        const item = { inputId: inpt.inputId, dataType: inpt.dataType };
        if (!('headerValueNo1' in rows[i])) item['headerValueNo1'] = dateText;
        if (!('headerValueNo2' in rows[i])) item['headerValueNo2'] = timeText;

        for (const key in rows[i]) {
          item[key] = rows[i][key];
        }
        items.push(item);
      }

      // 照合予定情報取込用登録API
      const ret = await postCompeareOrderInputAPI(items);
      if (!ret) {
        this.$emit('message', '通信エラー(照合予定情報取込)');
        return;
      }

      // 照合予定情報データセット
      for (let item of ret.data) {
        convertItemFormat(cmpr, item);
      }
      this.items = ret.data;
    },
    /**
     * 照合予定情報リスト検索
     * @param {number|string} inputId 取込ID
     * @param {!object} compares 照合情報
     */
    getCompareOrderList: async function (inputId, compare) {
      // 照合予定情報リストAPI
      const ret = await getCompareOrderListAPI(null, inputId);
      if (!ret) {
        this.$store.commit('setMessage', '通信エラー(照合予定情報リスト取得)');
        return;
      }

      this.items = ret.data;
      for (let item of this.items) {
        convertItemFormat(compare, item);
      }
    }
  }
};
</script>

<style>
div.container-fluid {
  height: 100%;
}
.b-table-sticky-header {
  max-height: none !important;
  -webkit-flex: 1 1 0;
  -ms-flex: 1 1 0;
  flex: 1 1 0;
}
</style>
